import { assign, setup } from "xstate";
import type { EebKioskConsentAcceptance } from "@ascleo/frontdesk-contracts/eeb/kiosk";
import type { EgkAttestationHandle } from "@ascleo/frontdesk-contracts/patient-info/egk-attestation";
import type { PatientInfoReview } from "@ascleo/frontdesk-contracts/patient-info/review";
import type { FieldResult } from "@ascleo/frontdesk-contracts/patient-info/submit";

/**
 * Insurance types supported by the check-in flow
 */
export type Insurance = "GKV" | "PKV" | "SELFPAY";

/**
 * Patient registration data collected during check-in
 */
export interface RegistrationData {
  familyName?: string;
  givenName?: string;
  dob?: string;
  gender?: "M" | "W" | "D" | "U";
  insurerId?: string | null;
  insurerName?: string;
  /**
   * Display name of the statutory insurer chosen in the registration insurer search. Shown in the
   * registration summary instead of the IK number (KC-76); never sent to the create call, which
   * identifies the insurer by `insurerId`.
   */
  insurerDisplayName?: string;
  policyNumber?: string;
}

/**
 * Field diff data from card read
 */
export interface FieldDiff {
  label?: string;
  path?: string;
  patientValue?: unknown;
  cardValue?: unknown;
  type?: string;
  changed?: boolean;
  parentPath?: string;
  parentPathLabel?: string;
  index?: number;
  isEnum?: boolean;
}

/**
 * Card data from eGK read - patient demographics
 */
export interface CardData {
  givenName: string;
  familyName: string;
  birthDate: string;
  gender: "male" | "female" | "other" | "unknown";
  insurance: {
    /** Patient insurance number (KVNR). */
    insuranceId: string;
    /** Authoritative cost-carrier IK read from the eGK. */
    insurerId?: string;
    insuranceName: string;
    insuranceType: "statutory" | "private" | "self-pay";
    validFrom: string;
  };
  address?: {
    street: string;
    houseNumber: string;
    zip: string;
    city: string;
    country: string;
  };
}

/**
 * Minimal matched patient info for disambiguation.
 * Only stores what's needed for fuzzy matching — no patient data
 * is displayed on screen (privacy: kiosk is a shared device).
 */
export interface MatchedPatientInfo {
  id: string;
  givenName: string;
}

/**
 * Check-in machine context
 */
export interface CheckInContext {
  /** Whether eEB (elektronische Ersatzbescheinigung) flow is enabled */
  eebEnabled?: boolean;
  /** Whether patient data differs from card data */
  hasDiff?: boolean;
  /** Actual diff data when card data differs from patient record */
  diffData?: FieldDiff[];
  /** Whether the insurance card is expired */
  cardExpired?: boolean;
  /** Selected insurance type */
  insurance?: Insurance;
  /** Patient ID once identified/created */
  patientId?: string;
  /** EEB (electronic verification) status */
  eebStatus?: "pending" | "ok" | "fail";
  /** Registration data for new patients */
  registration?: RegistrationData;
  /** Schedule/appointment ID */
  scheduleId?: string;
  /** Available appointments for selection (when multiple found) */
  appointments?: Array<{ scheduleId: string; time: string; type: string }>;
  /** KIM account info for eEB */
  kimAddress?: string;
  /** Practice name from KIM */
  practiceName?: string;
  /** Card data from eGK read for new patient registration */
  cardData?: CardData;
  /** Card read log ID for workflow operations */
  crlogId?: string;
  /** Matched patients for disambiguation (MATCHING status from card read) */
  matchedPatients?: MatchedPatientInfo[];
  // Quarterly coverage fields
  /** Whether patient has coverage proof from current quarter */
  hasCurrentQuarterCoverage?: boolean;
  /** Current quarter identifier (e.g., "2024-Q4") */
  coverageQuarter?: string;
  /** Whether patient has any previous GKV coverage on record */
  hasPreviousCoverage?: boolean;
  /** Previous insurer IKNR (for pre-fill) */
  previousInsurerIknr?: string;
  /** Previous insurer name (for pre-fill) */
  previousInsurerName?: string;
  /** Last card read date for display */
  lastCardReadDate?: string;
  /** Selected insurer IKNR (after selection/confirmation) */
  selectedIknr?: string;
  /** Selected insurer name */
  selectedInsurerName?: string;
  /** Whether patient granted consent for eEB */
  consentGranted?: boolean;
  /** Truthful durable acknowledgement returned by the signed eEB request endpoint. */
  eebAcceptance?: EebKioskConsentAcceptance;
  /**
   * Opaque eGK attestation from the card read, if this flow was established by an eGK card. Carried
   * through the shared flow and supplied to check-in / card-create so the server can commit it; it is
   * never persisted to storage, diagnostics, or platform adapters. Absent for manual flows.
   */
  egkAttestation?: EgkAttestationHandle;
  /** The capability-gated patient-info review, once loaded. Volatile — never persisted. */
  patientInfoReview?: PatientInfoReview;
  /** Per-field results of the one save, for the result screen. Volatile. */
  patientInfoResults?: readonly FieldResult[];
  /**
   * Which patient-info journey the patient completed, so the closing screen can say the true thing.
   * Absent when the review never ran (policy off, unavailable, ineligible) — the generic done copy
   * is correct in that case. Survives `clearPatientInfo`, which wipes patient data, not this fact.
   */
  patientInfoDoneMode?: "confirm" | "saved" | "skip";
}

/**
 * Check-in machine events
 */
export type CheckInEvent =
  | { type: "SELECT_YES" }
  | { type: "SELECT_NO" }
  | { type: "SELECT_UNSURE" }
  | {
      type: "CARD_OK";
      hasDiff?: boolean;
      diffData?: FieldDiff[];
      patientId?: string;
      insurance?: Insurance;
      crlogId?: string;
      egkAttestation?: EgkAttestationHandle;
    }
  | { type: "CARD_EXPIRED" }
  | { type: "CARD_MISSING" }
  | { type: "CONFIRM_DIFFS" }
  /**
   * The patient declines the card data on the diff review and checks in with the record unchanged
   * (KC-18). The pending card workflow is released (its crlog id is dropped, which makes the flow
   * cancel it exactly once) and the card attestation is not carried into the check-in.
   */
  | { type: "KEEP_RECORD" }
  /** The patient asks the reception for help after a failure the kiosk cannot recover (KC-19). */
  | { type: "RECEPTION_HELP" }
  | { type: "REQUEST_EEB" }
  | { type: "SELECT_INSURER" }
  | { type: "SELECT_GKV" }
  | { type: "SELECT_PKV_OR_SELFPAY" }
  | { type: "EEB_OK" }
  | { type: "EEB_FAIL" }
  | { type: "SUBMIT_SELF_PAY" }
  | { type: "BACK" }
  | {
      type: "REGISTER_FROM_CARD";
      cardData: CardData;
      crlogId: string;
      egkAttestation?: EgkAttestationHandle;
    }
  | { type: "CREATE_FROM_CARD_OK"; patientId: string }
  | { type: "CREATE_FROM_CARD_FAIL" }
  | {
      type: "MULTIPLE_PATIENTS_FOUND";
      matchedPatients: MatchedPatientInfo[];
      crlogId: string;
      insurance?: Insurance;
      egkAttestation?: EgkAttestationHandle;
    }
  | {
      type: "PATIENT_SELECTED";
      patientId: string;
      crlogId: string;
      hasDiff?: boolean;
      diffData?: FieldDiff[];
      egkAttestation?: EgkAttestationHandle;
    }
  | { type: "PATIENT_SELECTED_WITH_DIFF" }
  | { type: "BACK_TO_CARD_READING" }
  | {
      type: "PATIENT_CONFIRMED";
      patientId: string;
      insurance?: "GKV" | "PKV" | "SELFPAY" | "UNKNOWN";
    }
  | { type: "PATIENT_CREATED"; patientId: string }
  | { type: "PATIENT_CREATION_RESTRICTED" }
  | { type: "REGISTER" }
  | {
      type: "REGISTRATION_BASICS_NEXT";
      payload: {
        familyName: string;
        givenName: string;
        dob: string;
        gender: "M" | "W" | "D" | "U";
        insurance: Insurance;
        insurerName?: string;
      };
    }
  | { type: "INSURER_SELECTED"; insurerId: string; insurerName: string }
  | { type: "CHECKED_IN"; scheduleId: string }
  | {
      type: "SELECT_APPOINTMENT";
      appointments: Array<{ scheduleId: string; time: string; type: string }>;
    }
  | { type: "NO_APPOINTMENT" }
  | { type: "APPOINTMENT_SELECTED"; scheduleId: string }
  | { type: "KIM_LOADED"; kimAddress: string; practiceName: string }
  // Quarterly coverage events
  | {
      type: "QUARTERLY_CHECK_RESULT";
      hasCurrentQuarterCoverage: boolean;
      coverageQuarter?: string;
      hasPreviousCoverage: boolean;
      previousInsurerIknr?: string;
      previousInsurerName?: string;
      lastCardReadDate?: string;
    }
  | { type: "INSURER_CONFIRMED"; iknr: string; name: string }
  | { type: "INSURER_CHANGED" }
  | { type: "CONSENT_GRANTED"; acceptance: EebKioskConsentAcceptance }
  | { type: "CONSENT_DECLINED" }
  | { type: "SKIP_EEB" }
  // Insurer contact events
  | { type: "SUBMIT_EEB_REQUEST" }
  | { type: "INSURER_CONTACTED" }
  // Patient-info review events (post-commit optional subflow)
  | { type: "PATIENT_INFO_LOADED"; review: PatientInfoReview }
  | { type: "PATIENT_INFO_UNAVAILABLE" }
  | { type: "PATIENT_INFO_SUBMITTED"; results: readonly FieldResult[] }
  | { type: "PATIENT_INFO_CONFIRMED" }
  | { type: "PATIENT_INFO_EXPIRED" }
  | { type: "PATIENT_INFO_SKIP" }
  | { type: "PATIENT_INFO_CONTINUE" };

/**
 * Check-in state identifiers
 */
export type CheckInStateId =
  | "cardCheck"
  | "patientSelection"
  | "cardCheckin"
  | "registerFromCard"
  | "patientLookup"
  | "checkingIn"
  | "appointmentSelection"
  | "noAppointmentFound"
  | "eebPrompt"
  | "patientRegisterBasics"
  | "patientRegisterInsurer"
  | "patientRegisterDetails"
  | "patientCreationHandoff"
  | "receptionHelp"
  | "diffReview"
  | "insuranceSelection"
  | "eebRequest"
  | "selfPay"
  | "done"
  // Quarterly coverage states
  | "quarterlyCoverageCheck"
  | "insurerPreFilled"
  | "insurerSearchSelection"
  | "insurerContact"
  | "eebConsent"
  // Patient-info review states (post-commit optional subflow)
  | "patientInfoGate"
  | "patientInfoLoading"
  | "patientInfoReview"
  | "patientInfoResult"
  | "patientInfoExpired";

/**
 * Check-in XState machine
 *
 * This machine orchestrates the patient check-in flow:
 * 1. Ask if patient has an appointment
 * 2. Read insurance card (GKV) or manual lookup
 * 3. Handle patient identification/registration
 * 4. Process EEB verification for statutory insurance
 * 5. Complete check-in or redirect to booking
 */
export const checkInMachine = setup({
  types: {
    context: {} as CheckInContext,
    events: {} as CheckInEvent,
    input: {} as { eebEnabled?: boolean },
  },
  guards: {
    hasCardDiff: ({ event }) => event.type === "CARD_OK" && !!event.hasDiff,
    /** The flow was established by an eGK card, so the optional patient-info review may be offered. */
    hasEgkAttestation: ({ context }) => context.egkAttestation !== undefined,
    isGKV: ({ context }) => context.insurance === "GKV",
    isPKVOrSelfPayContext: ({ context }) =>
      context.insurance === "PKV" || context.insurance === "SELFPAY",
    isPKVOrSelfPay: ({ event }) =>
      event.type === "REGISTRATION_BASICS_NEXT" &&
      (event.payload.insurance === "PKV" ||
        event.payload.insurance === "SELFPAY"),
    isGKVRegistration: ({ event }) =>
      event.type === "REGISTRATION_BASICS_NEXT" &&
      event.payload.insurance === "GKV",
    isEebEnabled: ({ context }) => !!context.eebEnabled,
    isGKVFromSearch: ({ event }) =>
      event.type === "PATIENT_CONFIRMED" && event.insurance === "GKV",
    isPKVOrSelfPayFromSearch: ({ event }) =>
      event.type === "PATIENT_CONFIRMED" &&
      (event.insurance === "PKV" || event.insurance === "SELFPAY"),
    // Quarterly coverage guards
    hasCurrentQuarterCoverage: ({ event }) =>
      event.type === "QUARTERLY_CHECK_RESULT" &&
      event.hasCurrentQuarterCoverage,
    needsQuarterlyProofWithPrevious: ({ event }) =>
      event.type === "QUARTERLY_CHECK_RESULT" &&
      !event.hasCurrentQuarterCoverage &&
      event.hasPreviousCoverage,
    needsQuarterlyProofNoPrevious: ({ event }) =>
      event.type === "QUARTERLY_CHECK_RESULT" &&
      !event.hasCurrentQuarterCoverage &&
      !event.hasPreviousCoverage,
  },
  actions: {
    setHasDiff: assign({
      hasDiff: ({ event }) =>
        event.type === "CARD_OK" ? !!event.hasDiff : false,
      diffData: ({ event }) =>
        event.type === "CARD_OK" ? event.diffData : undefined,
      crlogId: ({ event }) =>
        event.type === "CARD_OK" ? event.crlogId : undefined,
    }),
    setPatientInfoReview: assign({
      patientInfoReview: ({ event }) =>
        event.type === "PATIENT_INFO_LOADED" ? event.review : undefined,
    }),
    setPatientInfoResults: assign({
      patientInfoResults: ({ event }) =>
        event.type === "PATIENT_INFO_SUBMITTED" ? event.results : undefined,
    }),
    setPatientInfoConfirmed: assign({
      patientInfoDoneMode: () => "confirm" as const,
    }),
    setPatientInfoSaved: assign({
      patientInfoDoneMode: () => "saved" as const,
    }),
    setPatientInfoSkipped: assign({
      patientInfoDoneMode: () => "skip" as const,
    }),
    clearPatientInfo: assign({
      // Wipe every volatile patient-info value (capability handle, review DTO, results) on any exit.
      egkAttestation: () => undefined,
      patientInfoReview: () => undefined,
      patientInfoResults: () => undefined,
    }),
    setCardExpired: assign({
      cardExpired: () => true,
    }),
    setPatientId: assign({
      patientId: ({ event }) => {
        if (
          event.type === "CREATE_FROM_CARD_OK" ||
          event.type === "PATIENT_CONFIRMED" ||
          event.type === "PATIENT_CREATED"
        ) {
          return event.patientId;
        }
        return undefined;
      },
    }),
    setPatientAndInsurance: assign({
      patientId: ({ event }) =>
        event.type === "PATIENT_CONFIRMED" ? event.patientId : undefined,
      insurance: ({ event }) => {
        if (event.type === "PATIENT_CONFIRMED") {
          if (event.insurance === "PKV") return "PKV" as Insurance;
          if (event.insurance === "SELFPAY") return "SELFPAY" as Insurance;
          return "GKV" as Insurance;
        }
        return undefined;
      },
    }),
    setPatientFromCard: assign({
      patientId: ({ event }) =>
        event.type === "CARD_OK" ? event.patientId : undefined,
      egkAttestation: ({ event }) =>
        event.type === "CARD_OK" ? event.egkAttestation : undefined,
      insurance: ({ event }) => {
        if (event.type === "CARD_OK" && event.insurance) {
          return event.insurance;
        }
        return "GKV" as Insurance; // Default to GKV for card-based check-in (eGK is GKV)
      },
    }),
    setInsuranceGKV: assign({
      insurance: () => "GKV" as Insurance,
    }),
    setInsurancePKV: assign({
      insurance: () => "PKV" as Insurance,
    }),
    setEebOk: assign({
      eebStatus: () => "ok" as const,
    }),
    setEebFail: assign({
      eebStatus: () => "fail" as const,
    }),
    setRegistrationBasics: assign({
      insurance: ({ event }) =>
        event.type === "REGISTRATION_BASICS_NEXT"
          ? event.payload.insurance
          : undefined,
      registration: ({ event }) =>
        event.type === "REGISTRATION_BASICS_NEXT"
          ? {
              familyName: event.payload.familyName,
              givenName: event.payload.givenName,
              dob: event.payload.dob,
              gender: event.payload.gender,
              insurerId: null,
              insurerName: event.payload.insurerName,
            }
          : undefined,
    }),
    setInsurerId: assign({
      registration: ({ context, event }) =>
        event.type === "INSURER_SELECTED"
          ? {
              ...context.registration,
              insurerId: event.insurerId,
              insurerDisplayName: event.insurerName,
            }
          : context.registration,
    }),
    releaseCardWorkflow: assign({
      crlogId: () => undefined,
      egkAttestation: () => undefined,
      hasDiff: () => false,
      diffData: () => undefined,
    }),
    setScheduleId: assign({
      scheduleId: ({ event }) => {
        if (
          event.type === "CHECKED_IN" ||
          event.type === "APPOINTMENT_SELECTED"
        ) {
          return event.scheduleId;
        }
        return undefined;
      },
    }),
    setAppointments: assign({
      appointments: ({ event }) =>
        event.type === "SELECT_APPOINTMENT" ? event.appointments : undefined,
    }),
    setKimInfo: assign({
      kimAddress: ({ event }) =>
        event.type === "KIM_LOADED" ? event.kimAddress : undefined,
      practiceName: ({ event }) =>
        event.type === "KIM_LOADED" ? event.practiceName : undefined,
    }),
    // Quarterly coverage actions
    setQuarterlyCoverageResult: assign({
      hasCurrentQuarterCoverage: ({ event }) =>
        event.type === "QUARTERLY_CHECK_RESULT"
          ? event.hasCurrentQuarterCoverage
          : undefined,
      coverageQuarter: ({ event }) =>
        event.type === "QUARTERLY_CHECK_RESULT"
          ? event.coverageQuarter
          : undefined,
      hasPreviousCoverage: ({ event }) =>
        event.type === "QUARTERLY_CHECK_RESULT"
          ? event.hasPreviousCoverage
          : undefined,
      previousInsurerIknr: ({ event }) =>
        event.type === "QUARTERLY_CHECK_RESULT"
          ? event.previousInsurerIknr
          : undefined,
      previousInsurerName: ({ event }) =>
        event.type === "QUARTERLY_CHECK_RESULT"
          ? event.previousInsurerName
          : undefined,
      lastCardReadDate: ({ event }) =>
        event.type === "QUARTERLY_CHECK_RESULT"
          ? event.lastCardReadDate
          : undefined,
    }),
    setInsurerConfirmed: assign({
      selectedIknr: ({ event }) =>
        event.type === "INSURER_CONFIRMED" ? event.iknr : undefined,
      selectedInsurerName: ({ event }) =>
        event.type === "INSURER_CONFIRMED" ? event.name : undefined,
    }),
    setInsurerSelected: assign({
      selectedIknr: ({ event }) =>
        event.type === "INSURER_SELECTED" ? event.insurerId : undefined,
      selectedInsurerName: ({ event }) =>
        event.type === "INSURER_SELECTED" ? event.insurerName : undefined,
    }),
    setConsentGranted: assign({
      consentGranted: () => true,
      eebAcceptance: ({ event }) =>
        event.type === "CONSENT_GRANTED" ? event.acceptance : undefined,
    }),
    setCardDataForRegistration: assign({
      cardData: ({ event }) =>
        event.type === "REGISTER_FROM_CARD" ? event.cardData : undefined,
      crlogId: ({ event }) =>
        event.type === "REGISTER_FROM_CARD" ? event.crlogId : undefined,
      egkAttestation: ({ event }) =>
        event.type === "REGISTER_FROM_CARD" ? event.egkAttestation : undefined,
    }),
    setMatchedPatients: assign({
      insurance: ({ event }) =>
        event.type === "MULTIPLE_PATIENTS_FOUND" ? event.insurance : undefined,
      egkAttestation: ({ event }) =>
        event.type === "MULTIPLE_PATIENTS_FOUND" ? event.egkAttestation : undefined,
      matchedPatients: ({ event }) =>
        event.type === "MULTIPLE_PATIENTS_FOUND"
          ? event.matchedPatients
          : undefined,
      crlogId: ({ event }) =>
        event.type === "MULTIPLE_PATIENTS_FOUND" ? event.crlogId : undefined,
    }),
    setPatientFromSelection: assign({
      patientId: ({ event }) =>
        event.type === "PATIENT_SELECTED" ? event.patientId : undefined,
      hasDiff: ({ event }) =>
        event.type === "PATIENT_SELECTED" ? !!event.hasDiff : false,
      diffData: ({ event }) =>
        event.type === "PATIENT_SELECTED" ? event.diffData : undefined,
      crlogId: ({ event }) =>
        event.type === "PATIENT_SELECTED" ? event.crlogId : undefined,
      egkAttestation: ({ event }) =>
        event.type === "PATIENT_SELECTED" ? event.egkAttestation : undefined,
      insurance: () => "GKV" as Insurance, // eGK card matches are always GKV
      matchedPatients: () => undefined, // Clear after selection
    }),
    resetContext: assign({
      hasDiff: () => undefined,
      cardExpired: () => undefined,
      insurance: () => undefined,
      patientId: () => undefined,
      eebStatus: () => undefined,
      registration: () => undefined,
      scheduleId: () => undefined,
      kimAddress: () => undefined,
      practiceName: () => undefined,
      // Reset quarterly coverage fields
      hasCurrentQuarterCoverage: () => undefined,
      coverageQuarter: () => undefined,
      hasPreviousCoverage: () => undefined,
      previousInsurerIknr: () => undefined,
      previousInsurerName: () => undefined,
      lastCardReadDate: () => undefined,
      selectedIknr: () => undefined,
      selectedInsurerName: () => undefined,
      consentGranted: () => undefined,
      eebAcceptance: () => undefined,
      // Reset card registration fields
      cardData: () => undefined,
      crlogId: () => undefined,
      matchedPatients: () => undefined,
    }),
  },
}).createMachine({
  id: "checkIn",
  initial: "cardCheck",
  context: ({ input }) => ({
    eebEnabled: input?.eebEnabled,
  }),
  states: {
    cardCheck: {
      on: {
        MULTIPLE_PATIENTS_FOUND: {
          target: "patientSelection",
          actions: "setMatchedPatients",
        },
        CARD_OK: [
          {
            guard: "hasCardDiff",
            target: "diffReview",
            actions: ["setHasDiff", "setPatientFromCard"],
          },
          // No diffs - skip cardCheckin confirmation and go directly to check-in
          { target: "checkingIn", actions: "setPatientFromCard" },
        ],
        CARD_EXPIRED: {
          target: "done",
          actions: "setCardExpired",
        },
        CARD_MISSING: { target: "patientLookup" },
        REGISTER_FROM_CARD: {
          target: "registerFromCard",
          actions: "setCardDataForRegistration",
        },
        PATIENT_CREATION_RESTRICTED: { target: "patientCreationHandoff" },
      },
    },
    patientSelection: {
      on: {
        BACK: { target: "cardCheck" },
        RECEPTION_HELP: { target: "receptionHelp" },
        PATIENT_SELECTED: [
          {
            guard: ({ event }) =>
              event.type === "PATIENT_SELECTED" && !!event.hasDiff,
            target: "diffReview",
            actions: "setPatientFromSelection",
          },
          {
            target: "checkingIn",
            actions: "setPatientFromSelection",
          },
        ],
      },
    },
    cardCheckin: {
      on: {
        BACK: { target: "cardCheck" },
        BACK_TO_CARD_READING: { target: "cardCheck" },
        CONFIRM_DIFFS: { target: "checkingIn" },
      },
    },
    registerFromCard: {
      on: {
        BACK: { target: "cardCheck" },
        CREATE_FROM_CARD_OK: {
          target: "patientInfoGate",
          actions: "setPatientId",
        },
        CREATE_FROM_CARD_FAIL: { target: "done" },
        PATIENT_CREATION_RESTRICTED: { target: "patientCreationHandoff" },
        RECEPTION_HELP: { target: "receptionHelp" },
      },
    },
    patientLookup: {
      on: {
        BACK: { target: "cardCheck" },
        PATIENT_CONFIRMED: [
          {
            guard: "isGKVFromSearch",
            target: "checkingIn",
            actions: "setPatientAndInsurance",
          },
          { target: "checkingIn", actions: "setPatientAndInsurance" },
        ],
        REGISTER: { target: "patientRegisterBasics" },
        RECEPTION_HELP: { target: "receptionHelp" },
      },
    },
    checkingIn: {
      on: {
        CHECKED_IN: [
          // GKV patients go through quarterly coverage check
          {
            guard: "isGKV",
            target: "quarterlyCoverageCheck",
            actions: "setScheduleId",
          },
          {
            guard: "isPKVOrSelfPayContext",
            target: "selfPay",
            actions: "setScheduleId",
          },
          // Non-GKV patients skip eEB entirely
          {
            target: "patientInfoGate",
            actions: "setScheduleId",
          },
        ],
        SELECT_APPOINTMENT: {
          target: "appointmentSelection",
          actions: "setAppointments",
        },
        NO_APPOINTMENT: { target: "noAppointmentFound" },
        RECEPTION_HELP: { target: "receptionHelp" },
      },
    },
    appointmentSelection: {
      on: {
        APPOINTMENT_SELECTED: [
          // GKV patients go through quarterly coverage check
          {
            guard: "isGKV",
            target: "quarterlyCoverageCheck",
            actions: "setScheduleId",
          },
          {
            guard: "isPKVOrSelfPayContext",
            target: "selfPay",
            actions: "setScheduleId",
          },
          // Non-GKV patients skip eEB entirely
          {
            target: "patientInfoGate",
            actions: "setScheduleId",
          },
        ],
        BACK: { target: "patientLookup" },
      },
    },
    noAppointmentFound: {
      on: {
        BACK: { target: "patientLookup" },
        RECEPTION_HELP: { target: "receptionHelp" },
      },
    },
    eebPrompt: {
      // Now serves as QR code display when consent is declined
      // Or legacy entry point for eEB flow
      on: {
        BACK: { target: "patientLookup" },
        REQUEST_EEB: { target: "eebConsent" },
        SELECT_INSURER: { target: "insurerSearchSelection" },
        SKIP_EEB: { target: "patientInfoGate" },
      },
    },
    patientRegisterBasics: {
      on: {
        BACK: { target: "patientLookup" },
        REGISTRATION_BASICS_NEXT: [
          {
            guard: "isGKVRegistration",
            target: "patientRegisterInsurer",
            actions: "setRegistrationBasics",
          },
          {
            guard: "isPKVOrSelfPay",
            target: "patientRegisterDetails",
            actions: "setRegistrationBasics",
          },
        ],
      },
    },
    patientRegisterInsurer: {
      on: {
        BACK: { target: "patientRegisterBasics" },
        RECEPTION_HELP: { target: "receptionHelp" },
        INSURER_SELECTED: {
          target: "patientRegisterDetails",
          actions: "setInsurerId",
        },
      },
    },
    patientRegisterDetails: {
      on: {
        BACK: [
          { guard: "isGKV", target: "patientRegisterInsurer" },
          { target: "patientRegisterBasics" },
        ],
        PATIENT_CREATED: [
          {
            // The statutory eEB exception is for known patients only. A newly created GKV
            // patient finishes registration without entering consent, even when eEB is enabled.
            guard: "isGKV",
            target: "patientInfoGate",
            actions: "setPatientId",
          },
          {
            target: "selfPay",
            actions: "setPatientId",
          },
        ],
        PATIENT_CREATION_RESTRICTED: { target: "patientCreationHandoff" },
        RECEPTION_HELP: { target: "receptionHelp" },
      },
    },
    patientCreationHandoff: {
      on: {
        BACK: { target: "cardCheck" },
      },
    },
    // Generic reception handoff after a failure the patient cannot resolve at the kiosk.
    receptionHelp: {
      on: {
        BACK: { target: "cardCheck" },
      },
    },
    diffReview: {
      on: {
        CONFIRM_DIFFS: { target: "checkingIn" },
        KEEP_RECORD: { target: "checkingIn", actions: "releaseCardWorkflow" },
        BACK: { target: "cardCheck" },
      },
    },
    insuranceSelection: {
      on: {
        SELECT_GKV: {
          target: "eebRequest",
          actions: "setInsuranceGKV",
        },
        SELECT_PKV_OR_SELFPAY: {
          target: "selfPay",
          actions: "setInsurancePKV",
        },
        BACK: { target: "cardCheck" },
      },
    },
    eebRequest: {
      on: {
        EEB_OK: {
          target: "patientInfoGate",
          actions: "setEebOk",
        },
        EEB_FAIL: {
          // On failure, show eebPrompt which has QR code as alternative
          target: "eebPrompt",
          actions: "setEebFail",
        },
        // The check-in is already committed; the reception verifies coverage instead (KC-41).
        SKIP_EEB: { target: "patientInfoGate" },
        BACK: { target: "eebConsent" },
      },
    },
    selfPay: {
      on: {
        SUBMIT_SELF_PAY: { target: "patientInfoGate" },
        BACK: { target: "insuranceSelection" },
      },
    },
    // Quarterly coverage check - determines if eEB is needed
    quarterlyCoverageCheck: {
      on: {
        QUARTERLY_CHECK_RESULT: [
          // Current quarter coverage exists - skip eEB
          {
            guard: "hasCurrentQuarterCoverage",
            target: "patientInfoGate",
            actions: "setQuarterlyCoverageResult",
          },
          // One server-proven active GKV coverage: consent presentation resolves
          // Haupt-IK and the recipient internally; the patient never selects an insurer.
          {
            guard: "needsQuarterlyProofWithPrevious",
            target: "eebConsent",
            actions: "setQuarterlyCoverageResult",
          },
          // Missing or ambiguous active coverage uses ordinary practice verification.
          {
            target: "patientInfoGate",
            actions: "setQuarterlyCoverageResult",
          },
        ],
        // Unproven quarter + no KIM account (decided in QuarterlyCoverageCheckStep):
        // the patient cannot self-serve an eEB at the kiosk → complete check-in
        // without eEB (the practice verifies coverage offline).
        SKIP_EEB: { target: "patientInfoGate" },
        BACK: { target: "patientLookup" },
      },
    },
    // Pre-filled insurer confirmation (returning GKV patients)
    insurerPreFilled: {
      on: {
        INSURER_CONFIRMED: {
          target: "insurerContact",
          actions: "setInsurerConfirmed",
        },
        INSURER_CHANGED: { target: "insurerSearchSelection" },
        BACK: { target: "quarterlyCoverageCheck" },
      },
    },
    // Searchable insurer selection (new GKV patients or changed insurer)
    insurerSearchSelection: {
      on: {
        INSURER_SELECTED: {
          target: "insurerContact",
          actions: "setInsurerSelected",
        },
        BACK: [
          // If they had previous coverage, go back to pre-fill
          {
            guard: ({ context }) => !!context.hasPreviousCoverage,
            target: "insurerPreFilled",
          },
          // Otherwise go back to quarterly check
          { target: "quarterlyCoverageCheck" },
        ],
      },
    },
    // Insurer contact instructions - shows how to request eEB
    insurerContact: {
      on: {
        // Submit eEB request - goes to consent first
        SUBMIT_EEB_REQUEST: { target: "eebConsent" },
        // Patient confirmed they contacted insurer manually - done
        INSURER_CONTACTED: { target: "patientInfoGate" },
        BACK: [
          // Go back to the appropriate insurer step
          {
            guard: ({ context }) => !!context.previousInsurerIknr,
            target: "insurerPreFilled",
          },
          { target: "insurerSearchSelection" },
        ],
      },
    },
    // eEB consent capture before sending request
    eebConsent: {
      on: {
        CONSENT_GRANTED: {
          target: "eebRequest",
          actions: "setConsentGranted",
        },
        CONSENT_DECLINED: {
          // Go back to insurer contact (patient can try other options)
          target: "insurerContact",
        },
        BACK: { target: "insurerContact" },
      },
    },
    // Patient-info review: an optional, automatic post-commit subflow. All eligible eGK exits route
    // here; the gate offers the review only when the flow carries an eGK attestation, and the server
    // is the final authority (an uncommitted/ineligible attestation makes loading fall through to done).
    patientInfoGate: {
      always: [
        { guard: "hasEgkAttestation", target: "patientInfoLoading" },
        { target: "done" },
      ],
    },
    patientInfoLoading: {
      on: {
        PATIENT_INFO_LOADED: {
          target: "patientInfoReview",
          actions: "setPatientInfoReview",
        },
        // Policy off/unavailable, expired, or any safe optional error: skip to done, clear state.
        PATIENT_INFO_UNAVAILABLE: {
          target: "done",
          actions: "clearPatientInfo",
        },
        PATIENT_INFO_SKIP: { target: "done", actions: "clearPatientInfo" },
      },
    },
    patientInfoReview: {
      on: {
        PATIENT_INFO_SUBMITTED: {
          target: "patientInfoResult",
          actions: "setPatientInfoResults",
        },
        // "Alles korrekt": nothing was staged, so there is no result to read. One deliberate tap
        // ends the review, per the accepted interaction budget.
        PATIENT_INFO_CONFIRMED: {
          target: "done",
          actions: ["setPatientInfoConfirmed", "clearPatientInfo"],
        },
        PATIENT_INFO_SKIP: {
          target: "done",
          actions: ["setPatientInfoSkipped", "clearPatientInfo"],
        },
        PATIENT_INFO_EXPIRED: {
          target: "patientInfoExpired",
          actions: "clearPatientInfo",
        },
      },
    },
    patientInfoResult: {
      on: {
        PATIENT_INFO_CONTINUE: {
          target: "done",
          actions: ["setPatientInfoSaved", "clearPatientInfo"],
        },
        PATIENT_INFO_EXPIRED: {
          target: "patientInfoExpired",
          actions: "clearPatientInfo",
        },
      },
    },
    // The capability's lifetime ran out. Everything shown or typed is dropped on entry; the
    // completed check-in is untouched, which is what the screen says. No done mode is recorded —
    // the patient finished no journey, and the expiry screen carries its own explanation.
    patientInfoExpired: {
      on: {
        PATIENT_INFO_CONTINUE: { target: "done", actions: "clearPatientInfo" },
      },
    },
    done: {},
  },
});
